import { useCallback, useEffect, useRef, useState } from 'react';

import { differenceInMilliseconds, getTime } from 'date-fns';
import { useInView } from 'react-intersection-observer';
import type { InViewHookResponse } from 'react-intersection-observer';

import type { OrderTrackingCardName } from 'src/app/types';

type UseTimeViewed = (
  /** Used to differentiate the actions */
  name: OrderTrackingCardName,

  /** A function that is fired once the component is seen on the screen */
  enterAction: (name: OrderTrackingCardName) => void,

  /** A function that is fired once the component exits the screen */
  exitAction: (name: OrderTrackingCardName, timeViewed: number) => void,

  /** A number in milliseconds that fires the exit action if the user never leaves the given component. */
  exitActionTimeout: number,

  /**
   * Decides the the threshold for how in view the component is.
   * @see: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#thresholds
   */
  intersectionThreshold: number
) => { ref: InViewHookResponse['ref'] };

/**
 * This returns a ref that can be used to associate an element with a relevant Intersection Observer instance.
 *
 * @usage
 * ```ts
 * const enterAction = name => print('entered!');
 * const exitAction = (name, timeViewed) => print('exited:', timeViewed);
 * const exampleBind = useTimeViewed('example', enterAction, exitAction, 0.35);
 *
 * return (
 *   <div {...exampleBind} />
 * )
 * ```
 */
export const useTimeViewed: UseTimeViewed = (
  name,
  enterAction,
  exitAction,
  exitActionTimeout,
  intersectionThreshold
) => {
  const [startTime, setStartTime] = useState<number | null>(null);
  const { inView, ref } = useInView({ threshold: intersectionThreshold });
  const calledEnterAction = useRef(false);
  const calledExitAction = useRef(false);
  const timeoutAction = useRef<null | number>(null);

  const callExitAction = useCallback(
    (secondsSinceEnter: number) => {
      exitAction(name, secondsSinceEnter);
      calledExitAction.current = true;
    },
    [exitAction, name]
  );

  useEffect(() => {
    if (inView && !calledEnterAction.current) {
      enterAction(name);
      calledEnterAction.current = true;
      setStartTime(getTime(Date.now()));
      timeoutAction.current = window.setTimeout(() => {
        callExitAction(exitActionTimeout / 1000.0);
      }, exitActionTimeout);
    } else {
      // eslint-disable-next-line no-lonely-if
      if (startTime && !calledExitAction.current) {
        if (timeoutAction.current) window.clearTimeout(timeoutAction.current);
        const timeViewed = differenceInMilliseconds(Date.now(), startTime);
        callExitAction(timeViewed);
      }
    }

    return () => {
      if (timeoutAction.current) clearTimeout(timeoutAction.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return { ref };
};
